import { Heading } from "@ariakit/react";
import { Link } from "wouter";
import { toLocaleDateString } from "../../lib/date-format";
import { atomic } from "../atomic.css";
import { ContentView } from "../content-view";
import { createDocsPages } from "../create-docs-pages";
import { ErrorView } from "../error-view";
import { Icon } from "../icon";
import { LoadingView } from "../loading-view";
import * as css from "./style.css";

function idToDate(filename: string) {
  const dateString = /^(?<date>\d{4}-\d{2}-\d{2})/.exec(filename)?.groups?.date;
  return dateString ? new Date(dateString) : new Date("2024-12-22");
}

export type TextPageData = {
  id: string;
  data: {
    name: string;
    created_at?: string;
    updated_at?: string;
    author?: string;
  };
  content: string;
};

function TextPage(props: {
  coverImageSrc?: string;
  name: string;
  author?: string;
  publishedAt?: Date | string | null;
  updatedAt?: Date | string | null;
  content: string;
  upgradeToVersion?: string;
  backHref: string;
}) {
  const publishedDate =
    props.publishedAt &&
    toLocaleDateString(props.publishedAt, { dateStyle: "long" });

  const updatedDate =
    props.updatedAt &&
    `Last updated: ${toLocaleDateString(props.updatedAt, {
      dateStyle: "long",
    })}`;

  return (
    <ContentView heading="Article" icon="back" backHref={props.backHref}>
      <header className={atomic({ marginBlockEnd: 32 })}>
        <Heading
          className={atomic({
            fontFamily: "arboria",
            fontStyle: "italic",
            textTransform: "uppercase",
            fontSize: 32,
            lineHeight: 1,
          })}
        >
          {props.name}
        </Heading>

        <p className={atomic({ marginBlockStart: 12 })}>
          <em>
            {[props.author, publishedDate, updatedDate]
              .filter((i) => !!i)
              .join(", ")}
          </em>
        </p>
      </header>

      <div
        className={css.content}
        dangerouslySetInnerHTML={{ __html: props.content }}
      />
    </ContentView>
  );
}

function createCustomDocsPages(opts: { title: string; fetchBasePath: string }) {
  return createDocsPages<TextPageData>({
    title: opts.title,
    fetchBasePath: opts.fetchBasePath,
    renderListingPage(pages) {
      return (
        <ContentView heading={opts.title} icon="back" backHref="~/">
          <ul
            className={atomic({
              borderBlockStart: "solid",
            })}
          >
            {pages.map((page) => {
              return (
                <li
                  key={page.id}
                  className={atomic({
                    borderBlockEnd: { last: "none", default: "dashed" },
                  })}
                >
                  <Link
                    href={`/${page.id}`}
                    className={atomic({
                      display: "grid",
                      gridTemplateColumns: "fraction-auto",
                      alignItems: "center",
                      gap: 8,
                      paddingBlockStart: 20,
                      paddingBlockEnd: 18,
                      transition: "all",
                      backgroundColor: { hover: "background-secondary" },
                      paddingInlineStart: { hover: 16 },
                    })}
                  >
                    <div>
                      <div
                        className={atomic({
                          fontFamily: "barlow-condensed",
                          fontWeight: "semi-bold",
                          fontSize: 24,
                        })}
                      >
                        {page.data.name}
                      </div>
                      <div className={atomic({ lineHeight: 1.5 })}>
                        {toLocaleDateString(
                          page.data.created_at ?? idToDate(page.id),
                          {
                            dateStyle: "long",
                          },
                        )}
                      </div>
                    </div>

                    <Icon name="forward" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </ContentView>
      );
    },
    renderListingError() {
      return (
        <ContentView heading={opts.title} icon="back" backHref="~/">
          <ErrorView />
        </ContentView>
      );
    },
    renderListingPending() {
      return (
        <ContentView heading={opts.title} icon="back" backHref="~/">
          <LoadingView />
        </ContentView>
      );
    },

    renderPage(page) {
      return (
        <TextPage
          name={page.data.name}
          author={page.data.author}
          publishedAt={page.data.created_at ?? idToDate(page.id)}
          updatedAt={page.data.updated_at}
          content={page.content}
          backHref="/"
        />
      );
    },
    renderPageError() {
      return (
        <ContentView heading="Article" icon="back" backHref="/">
          <ErrorView />
        </ContentView>
      );
    },
    renderPagePending() {
      return (
        <ContentView heading="Article" icon="back" backHref="/">
          <LoadingView />
        </ContentView>
      );
    },
  });
}

export const HelpPages = createCustomDocsPages({
  title: "Help",
  fetchBasePath: "/docs/help",
});

export const ChangelogPages = createCustomDocsPages({
  title: "Changelog",
  fetchBasePath: "/docs/changelog",
});

export const LegalPages = createCustomDocsPages({
  title: "Legal",
  fetchBasePath: "/docs/legal",
});
